import { TemporalDayOfWeek } from "@redotech/util/temporal";
import { Team } from "../team";
import {
  CheckoutProbabilitySegment,
  CustomerReturnRateSegment,
  CustomerTagsSegment,
  CustomerTagsSegmentNodeIdentifier,
  DatetimeRangeSegment,
  DeliveryDistanceMilesSegment,
  DeliveryLocationSegment,
  ItemQuantitySegment,
  LineItemPropertiesSegment,
  OrderCountSegment,
  OriginLocationSegment,
  ProductCollectionsSegment,
  ProductSkuSegment,
  ProductTagsSegment,
  ProductTagsSegmentNodeIdentifier,
  ProductVendorSegment,
  Segment,
  SegmentableCheckoutCharacteristic,
  SegmentListMultipleItemMembershipOperator,
  SegmentListSingleItemMembershipOperator,
  SegmentNumberOperator,
  SegmentTimeOfDayOperator,
  TimeOfDaySegment,
} from "./checkout-tree";

export const segmentableCharacteristicLabel: Record<
  SegmentableCheckoutCharacteristic,
  string
> = {
  [SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES]:
    "Delivery distance",
  [SegmentableCheckoutCharacteristic.ORDER_COUNT]: "Order count",
  [SegmentableCheckoutCharacteristic.DELIVERY_LOCATION]: "Delivery location",
  [SegmentableCheckoutCharacteristic.CUSTOMER_TAGS]: "Customer tags",
  [SegmentableCheckoutCharacteristic.PRODUCT_TAGS]: "Product tags",
  [SegmentableCheckoutCharacteristic.PRODUCT_VENDOR]: "Product vendor",
  [SegmentableCheckoutCharacteristic.PRODUCT_SKU]: "Product SKU",
  [SegmentableCheckoutCharacteristic.ITEM_QUANTITY]: "Item quantity",
  [SegmentableCheckoutCharacteristic.ORIGIN_LOCATION]: "Origin location",
  [SegmentableCheckoutCharacteristic.CUSTOMER_RETURN_RATE]:
    "Customer return rate",
  [SegmentableCheckoutCharacteristic.TIME_OF_DAY]: "Time of day",
  [SegmentableCheckoutCharacteristic.DATETIME_RANGE]: "Datetime range",
  [SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY]:
    "Checkout probability score",
  [SegmentableCheckoutCharacteristic.PRODUCT_COLLECTIONS]:
    "Product collections",
  [SegmentableCheckoutCharacteristic.LINE_ITEM_PROPERTIES]:
    "Line item properties",
};

const defaultDeliveryDistanceMilesSegment: DeliveryDistanceMilesSegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES,
  miles: 100,
  operator: SegmentNumberOperator.LESS_THAN,
};

const defaultOrderCountSegment: OrderCountSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.ORDER_COUNT,
  numOrders: 1,
  operator: SegmentNumberOperator.LESS_THAN,
};

const defaultDeliveryLocationSegment: DeliveryLocationSegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.DELIVERY_LOCATION,
  locationCountry: {
    countries: ["US"],
    operator: SegmentListSingleItemMembershipOperator.IN,
  },
};

const defaultCustomerTagsSegment: CustomerTagsSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.CUSTOMER_TAGS,
  tags: [],
  operator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  customerTagsSegmentNodeIdentifier:
    CustomerTagsSegmentNodeIdentifier.CUSTOMER_TAGS_SEGMENT_NODE_ONE,
};

const defaultProductVendorSegment: ProductVendorSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.PRODUCT_VENDOR,
  vendors: [],
  operator: SegmentListSingleItemMembershipOperator.IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
};

const defaultProductSkuSegment: ProductSkuSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.PRODUCT_SKU,
  skus: [],
  operator: SegmentListSingleItemMembershipOperator.IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  requireExactMatch: true,
};

const defaultProductTagsSegment: ProductTagsSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.PRODUCT_TAGS,
  tags: [],
  operator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  productTagsSegmentNodeIdentifier:
    ProductTagsSegmentNodeIdentifier.PRODUCT_TAGS_SEGMENT_NODE_ONE,
};

const defaultItemQuantitySegment: ItemQuantitySegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.ITEM_QUANTITY,
  quantity: 2,
  operator: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
  exclusions: { skus: ["x-redo"] },
};

const defaultOriginLocationSegment: OriginLocationSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.ORIGIN_LOCATION,
  originAddress: {
    address1: null,
    address2: null,
    city: null,
    province: null,
    postalCode: null,
    country: null,
  },
};

const defaultCustomerReturnRateSegment: CustomerReturnRateSegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.CUSTOMER_RETURN_RATE,
  returnRatePercentage: 50,
  operator: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
};

function getDefaultTimeOfDaySegment(
  ianaTimezone?: string | undefined,
): TimeOfDaySegment {
  return {
    segmentableCharacteristic: SegmentableCheckoutCharacteristic.TIME_OF_DAY,
    time: { hh: 15, mm: 0 },
    weekdays: [
      TemporalDayOfWeek.MONDAY,
      TemporalDayOfWeek.TUESDAY,
      TemporalDayOfWeek.WEDNESDAY,
      TemporalDayOfWeek.THURSDAY,
      TemporalDayOfWeek.FRIDAY,
      TemporalDayOfWeek.SATURDAY,
      TemporalDayOfWeek.SUNDAY,
    ],
    ianaTimezone: ianaTimezone ?? "America/New_York",
    operator: SegmentTimeOfDayOperator.BEFORE,
  };
}

function getDefaultDatetimeRangeSegment(
  ianaTimezone?: string | undefined,
): DatetimeRangeSegment {
  const now = Temporal.Now.plainDateTimeISO().with({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
    microsecond: 0,
  });
  return {
    segmentableCharacteristic: SegmentableCheckoutCharacteristic.DATETIME_RANGE,
    startDateTime: now.toString(),
    endDateTime: now.toString(),
    ianaTimezone: ianaTimezone ?? "America/New_York",
  };
}

export enum DefaultCheckoutProbabilityBucket {
  COLD = 0,
  COOL = 1,
  WARM = 2,
  HOT = 3,
}

// lower thresholds
export const defaultCheckoutProbabilityLowerThresholds: Record<
  DefaultCheckoutProbabilityBucket,
  number
> = {
  [DefaultCheckoutProbabilityBucket.COLD]: 0,
  [DefaultCheckoutProbabilityBucket.COOL]: 15,
  [DefaultCheckoutProbabilityBucket.WARM]: 35,
  [DefaultCheckoutProbabilityBucket.HOT]: 70,
};

// upper thresholds
export const defaultCheckoutProbabilityUpperThresholds: Record<
  DefaultCheckoutProbabilityBucket,
  number
> = {
  [DefaultCheckoutProbabilityBucket.COLD]: 15,
  [DefaultCheckoutProbabilityBucket.COOL]: 35,
  [DefaultCheckoutProbabilityBucket.WARM]: 70,
  [DefaultCheckoutProbabilityBucket.HOT]: 100,
};

export const defaultCheckoutProbabilityExpressions: Record<
  DefaultCheckoutProbabilityBucket,
  CheckoutProbabilitySegment["expressions"]
> = {
  // Cold bucket: 0% <= p < 15%
  [DefaultCheckoutProbabilityBucket.COLD]: [
    {
      probability:
        defaultCheckoutProbabilityLowerThresholds[
          DefaultCheckoutProbabilityBucket.COLD
        ],
      operator: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
    },
    {
      probability:
        defaultCheckoutProbabilityUpperThresholds[
          DefaultCheckoutProbabilityBucket.COLD
        ],
      operator: SegmentNumberOperator.LESS_THAN,
    },
  ],
  // Cool bucket: 15% <= p < 35%
  [DefaultCheckoutProbabilityBucket.COOL]: [
    {
      probability:
        defaultCheckoutProbabilityLowerThresholds[
          DefaultCheckoutProbabilityBucket.COOL
        ],
      operator: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
    },
    {
      probability:
        defaultCheckoutProbabilityUpperThresholds[
          DefaultCheckoutProbabilityBucket.COOL
        ],
      operator: SegmentNumberOperator.LESS_THAN,
    },
  ],
  // Warm bucket: 35% <= p < 70%
  [DefaultCheckoutProbabilityBucket.WARM]: [
    {
      probability:
        defaultCheckoutProbabilityLowerThresholds[
          DefaultCheckoutProbabilityBucket.WARM
        ],
      operator: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
    },
    {
      probability:
        defaultCheckoutProbabilityUpperThresholds[
          DefaultCheckoutProbabilityBucket.WARM
        ],
      operator: SegmentNumberOperator.LESS_THAN,
    },
  ],
  // Hot bucket: 70% <= p <= 100%
  [DefaultCheckoutProbabilityBucket.HOT]: [
    {
      probability:
        defaultCheckoutProbabilityLowerThresholds[
          DefaultCheckoutProbabilityBucket.HOT
        ],
      operator: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
    },
    {
      probability:
        defaultCheckoutProbabilityUpperThresholds[
          DefaultCheckoutProbabilityBucket.HOT
        ],
      operator: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
    },
  ],
};

export function getDefaultCheckoutProbabilityBucket(
  probability: number,
): DefaultCheckoutProbabilityBucket {
  for (const bucket of Object.values(DefaultCheckoutProbabilityBucket)) {
    if (typeof bucket !== "number") {
      continue;
    }

    const lowerThreshold = defaultCheckoutProbabilityLowerThresholds[bucket];
    const upperThreshold = defaultCheckoutProbabilityUpperThresholds[bucket];

    // Special case for HOT bucket to include probability === 100
    if (bucket === DefaultCheckoutProbabilityBucket.HOT) {
      if (probability >= lowerThreshold && probability <= upperThreshold) {
        return bucket;
      }
    } else if (probability >= lowerThreshold && probability < upperThreshold) {
      return bucket;
    }
  }

  throw new Error("No bucket found for probability");
}

const defaultIntentSegment: CheckoutProbabilitySegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY,
  expressions:
    defaultCheckoutProbabilityExpressions[
      DefaultCheckoutProbabilityBucket.WARM
    ],
};

const defaultProductCollectionsSegment: ProductCollectionsSegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.PRODUCT_COLLECTIONS,
  collectionIds: [],
  collectionListOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
};

const defaultLineItemPropertiesSegment: LineItemPropertiesSegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.LINE_ITEM_PROPERTIES,
  properties: [],
  propertiesListOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
};

export const CHECKOUT_PROBABILITY_LINE_ITEM_PROPERTY_KEY =
  "_redo_intent_engine_checkout_probability";

export function getDefaultSegments(
  team?: Team | undefined,
): Record<SegmentableCheckoutCharacteristic, Segment> {
  const ianaTimezone = team?._shopify?.iana_timezone ?? undefined;

  return {
    [SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES]:
      defaultDeliveryDistanceMilesSegment,
    [SegmentableCheckoutCharacteristic.ORDER_COUNT]: defaultOrderCountSegment,
    [SegmentableCheckoutCharacteristic.DELIVERY_LOCATION]:
      defaultDeliveryLocationSegment,
    [SegmentableCheckoutCharacteristic.CUSTOMER_TAGS]:
      defaultCustomerTagsSegment,
    [SegmentableCheckoutCharacteristic.PRODUCT_VENDOR]:
      defaultProductVendorSegment,
    [SegmentableCheckoutCharacteristic.PRODUCT_SKU]: defaultProductSkuSegment,
    [SegmentableCheckoutCharacteristic.PRODUCT_TAGS]: defaultProductTagsSegment,
    [SegmentableCheckoutCharacteristic.ITEM_QUANTITY]:
      defaultItemQuantitySegment,
    [SegmentableCheckoutCharacteristic.ORIGIN_LOCATION]:
      defaultOriginLocationSegment,
    [SegmentableCheckoutCharacteristic.CUSTOMER_RETURN_RATE]:
      defaultCustomerReturnRateSegment,
    [SegmentableCheckoutCharacteristic.TIME_OF_DAY]:
      getDefaultTimeOfDaySegment(ianaTimezone),
    [SegmentableCheckoutCharacteristic.DATETIME_RANGE]:
      getDefaultDatetimeRangeSegment(ianaTimezone),
    [SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY]:
      defaultIntentSegment,
    [SegmentableCheckoutCharacteristic.PRODUCT_COLLECTIONS]:
      defaultProductCollectionsSegment,
    [SegmentableCheckoutCharacteristic.LINE_ITEM_PROPERTIES]:
      defaultLineItemPropertiesSegment,
  };
}
